import {
  CoverageUuid,
  EndorsementUuid,
  SubCoverageUuid,
  SubjectivityUuid,
} from '@converge/data';
import {
  IUnderwritingCoverage,
  IUnderwritingEndorsement,
  IUnderwritingSubCoverage,
  IUnderwritingSubjectivity,
} from './types';

import { convergeLogger } from '@converge/config';

export enum QuoteLineItemType {
  Coverage = 'coverage',
  Endorsement = 'endorsement',
  SubCoverage = 'subCoverage',
  Subjectivity = 'subjectivity',
}

export interface IQuoteLineItemFeatures {
  enableRetroactiveDateOrFullPriorActs: boolean;
  enableMediaEventCoverage: boolean;
  enableRestorationPeriodDays: boolean;
  enableWaitingPeriodDays: boolean;
  enableWaitingPeriodHours: boolean;
  enableRetention: boolean;
}

export type IQuoteLineItem = Omit<
  | IUnderwritingEndorsement
  | IUnderwritingCoverage
  | IUnderwritingEndorsement
  | IUnderwritingSubCoverage
  | IUnderwritingSubjectivity,
  'features'
> & {
  features?: IQuoteLineItemFeatures;
};

export class QuoteLineItemFeatures implements IQuoteLineItemFeatures {
  toJSON(): IQuoteLineItemFeatures {
    return {
      enableRetroactiveDateOrFullPriorActs:
        this.enableRetroactiveDateOrFullPriorActs,
      enableMediaEventCoverage: this.enableMediaEventCoverage,
      enableRestorationPeriodDays: this.enableRestorationPeriodDays,
      enableWaitingPeriodDays: this.enableWaitingPeriodDays,
      enableWaitingPeriodHours: this.enableWaitingPeriodHours,
      enableRetention: this.enableRetention,
    };
  }

  /* Begin business logic */

  get enableRetroactiveDateOrFullPriorActs() {
    if (this.isEndorsement) {
      if (
        [
          EndorsementUuid.MediaEventLiabilityEndorsement,
          EndorsementUuid.TechnologyErrorsAndOmissions,
          // EndorsementUuid.MiscellaneousProfessionalLiabilityErrorsAndOmissions,
        ].includes(this.endorsementUuid)
      ) {
        return true;
      }
    }

    if (this.isCoverage) {
      return [
        CoverageUuid.NetworkSecurityPrivacyLiability,
        CoverageUuid.RegulatoryProceedings,
        CoverageUuid.PciDssEvents,
      ].includes(this.coverageUuid);
    }

    return false;
  }

  get enableMediaEventCoverage() {
    if (this.uuid === EndorsementUuid.MediaEventLiabilityEndorsement) {
      return true;
    }

    return false;
  }

  get enableRestorationPeriodDays() {
    return [
      CoverageUuid.BusinessInterruption,
      CoverageUuid.ContingentBusinessInterruption,
      SubCoverageUuid.NonItProviders,
      CoverageUuid.ReputationalHarm,
    ].includes(this.coverageUuid ?? this.subCoverageUuid);
  }

  get enableWaitingPeriodDays() {
    return [CoverageUuid.ReputationalHarm].includes(this.coverageUuid);
  }

  get enableWaitingPeriodHours() {
    return [
      CoverageUuid.BusinessInterruption,
      CoverageUuid.ContingentBusinessInterruption,
      SubCoverageUuid.NonItProviders,
    ].includes(this.coverageUuid ?? this.subCoverageUuid);
  }

  get enableRetention() {
    if (this.isSubCoverage) {
      return ![SubCoverageUuid.CriminalReward].includes(this.subCoverageUuid);
    }

    if (this.isEndorsement && this.endorsement.nonPremiumBearing) {
      return false;
    }

    return true;
  }

  /* End business logic */

  static from(type: QuoteLineItemType, lineItem: IQuoteLineItem) {
    return new QuoteLineItemFeatures(type, lineItem);
  }

  constructor(
    public type: QuoteLineItemType,
    public lineItem: IQuoteLineItem
  ) {}

  get uuid() {
    return this.lineItem.uuid;
  }

  get isKnownUuid() {
    return (
      this.isEndorsement ||
      this.isCoverage ||
      this.isSubCoverage ||
      this.isSubjectivity
    );
  }

  /* Endorsement */

  get endorsement() {
    return this.lineItem as IUnderwritingEndorsement;
  }

  get endorsementUuid() {
    return this.uuid as EndorsementUuid;
  }

  get isEndorsement() {
    return Object.values(EndorsementUuid).includes(this.endorsementUuid);
  }

  /* Coverage */

  get coverage() {
    return this.lineItem as IUnderwritingCoverage;
  }

  get coverageUuid() {
    return this.uuid as CoverageUuid;
  }

  get isCoverage() {
    return Object.values(CoverageUuid).includes(this.uuid as CoverageUuid);
  }

  /* SubCoverage */

  get subCoverage() {
    return this.lineItem as IUnderwritingSubCoverage;
  }

  get subCoverageUuid() {
    return this.uuid as SubCoverageUuid;
  }

  get isSubCoverage() {
    return Object.values(SubCoverageUuid).includes(
      this.uuid as SubCoverageUuid
    );
  }

  /* Subjectivity */

  get subjectivity() {
    return this.lineItem as IUnderwritingSubjectivity;
  }

  get subjectivityUuid() {
    return this.uuid as SubjectivityUuid;
  }

  get isSubjectivity() {
    if (this.type === QuoteLineItemType.Subjectivity) {
      const isKnownUuid = Object.values(SubjectivityUuid).includes(
        this.uuid as SubjectivityUuid
      );

      if (!isKnownUuid) {
        convergeLogger.warn(`Unknown subjectivity uuid: ${this.uuid}`);
      }

      return true;
    }

    return false;
  }
}
